import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { FlexBox, ScrollBox } from '../box';
import { Divider } from '../divider';
import { useMounted, useOverflow, useSize } from '../hooks';
import { Link } from '../link';
import { Menu } from '../menu';
import { Skeleton } from '../skeleton';
import { Tooltip } from '../tooltip';
import { Typography, TypographyWithTooltip } from '../typography';
import { flattenChildren } from '../utils';
import { WidgetListColumnSize } from './types';
export { WidgetListColumnSize };
export const WidgetListContext = React.createContext(undefined);
export const MIN_SECTIONS = 2;
export const MAX_SECTIONS = 3;
const noopRegisterRowWithActions = () => () => undefined;
export const WidgetList = (props) => {
    const internalProps = props;
    const children = flattenChildren(props.children);
    const [nestedRowsWithActions, setNestedRowsWithActions] = React.useState(0);
    const [hasDragList, setHasDragList] = React.useState(false);
    const hasActions = children
        .filter(isChildRow)
        .some((child) => child.props.actions);
    const orientation = (() => {
        if (hasDragList ||
            hasActions ||
            nestedRowsWithActions > 0 ||
            internalProps.forceListOrientation) {
            return 'list';
        }
        return props.orientation ?? 'horizontal';
    })();
    const validateSections = () => {
        const numSections = children.filter(isChildSection).length;
        if (numSections > 0 &&
            // NOTE: cannot validate MIN_SECTIONS because they can be rendered condionally
            /* numSections < MIN_SECTIONS || */ numSections > MAX_SECTIONS) {
            console.warn(`[WidgetList] Invalid number of sections: ${numSections}. Allowed number: ${MAX_SECTIONS}`);
        }
        if (numSections > 0 && orientation === 'list') {
            console.warn('[WidgetList] Sections are not allowed in list-orientation.');
        }
    };
    const validateOrientation = () => {
        if (orientation === 'list') {
            if (props.orientation) {
                console.warn(`[WidgetList] Passed orientation prop '${props.orientation}' will be ignored due to formatting as list.`);
            }
        }
        if (orientation === 'vertical') {
            if (props.columnSize) {
                console.warn(`[WidgetList] Passed columnSize prop '${props.columnSize}' will be ignored due to orientation="vertical".`);
            }
        }
    };
    if (process.env.NODE_ENV === 'development') {
        validateOrientation();
        validateSections();
    }
    const registerRowWithActions = React.useCallback(() => {
        setNestedRowsWithActions((prev) => prev + 1);
        return () => setNestedRowsWithActions((prev) => prev - 1);
    }, []);
    const registerWithDragList = React.useCallback(() => {
        setHasDragList(true);
        return () => setHasDragList(false);
    }, []);
    const contextValue = React.useMemo(() => ({
        orientation,
        columnSize: props.columnSize ?? '50%',
        registerRowWithActions,
        registerWithDragList,
        hasDragList
    }), [
        orientation,
        props.columnSize,
        registerRowWithActions,
        registerWithDragList,
        hasDragList
    ]);
    return (_jsx(WidgetListContext.Provider, { value: contextValue, children: _jsx(ScrollBox, { shadowType: internalProps.footerAction ? 'small' : 'large', children: internalProps.loading ? _jsx(Loader, {}) : children }) }));
};
const Row = (props) => {
    const internalProps = props;
    const context = React.useContext(WidgetListContext);
    const hasActions = (props.actions?.filter(Boolean).length ?? 0) > 0;
    React.useEffect(() => {
        if (hasActions) {
            return void context?.registerRowWithActions();
        }
    }, [hasActions, context]);
    const flexGrow = props.stretchHeight ? 1 : 0;
    const orientation = internalProps.orientation ?? context?.orientation ?? 'horizontal';
    const columnSize = props.columnSize ?? context?.columnSize ?? '50%';
    const vAlign = (() => {
        if (props.vAlign) {
            return props.vAlign;
        }
        switch (orientation) {
            case 'horizontal':
            case 'vertical':
                return 'flex-start';
            case 'list':
                return 'center';
        }
    })();
    const actions = props.actions && (_jsx(FlexBox, { hAlign: "flex-end", vAlign: vAlign, flexShrink: 1, flexGrow: 0, flexBasis: 30, pr: 1, children: _jsx("div", { children: _jsx(Menu, { item: props.item, actions: props.actions, icon: "IconMoreVert", iconProps: { fontSize: 'small' } }) }) }));
    const children = props.children === '' ? '-' : (props.children ?? '-');
    const flex = (() => {
        switch (columnSize) {
            case '50%':
                return { left: 5, right: 5 };
            case '65%':
                return { left: 6.5, right: 3.5 };
        }
    })();
    switch (orientation) {
        case 'horizontal':
            return (_jsxs(FlexBox, { px: 1, vAlign: vAlign, sx: { flexGrow }, children: [props.label && (_jsx(FlexBox, { autoShrink: true, p: 1, flex: flex.left, children: _jsx(Column, { color: "text.secondary", children: props.label }) })), _jsx(FlexBox, { autoShrink: true, p: 1, flex: flex.right, children: _jsx(Column, { variant: "subtitle2", children: children }) }), actions] }));
        case 'vertical':
            return (_jsxs(FlexBox, { stack: true, px: 1, pb: 1, vAlign: vAlign, sx: { flexGrow }, children: [props.label && (_jsx(FlexBox, { px: 1, py: 0.5, stretch: true, children: _jsx(Column, { color: "text.secondary", children: props.label }) })), _jsx(FlexBox, { px: 1, py: 0.5, stretch: true, children: _jsx(Column, { variant: "subtitle2", children: children }) })] }));
        case 'list':
            return (_jsxs(_Fragment, { children: [_jsxs(FlexBox, { px: 2, sx: { flexGrow, width: '100%' }, children: [props.label && (_jsx(FlexBox, { autoShrink: true, p: 1, vAlign: vAlign, flex: flex.left, children: _jsx(Column, { color: "text.secondary", children: props.label }) })), _jsx(FlexBox, { autoShrink: true, p: 1, vAlign: vAlign, flex: flex.right, children: _jsx(Column, { variant: "subtitle2", children: children }) }), actions] }), hasActions &&
                        !context?.hasDragList &&
                        !internalProps.withoutDivider && _jsx(Divider, {})] }));
    }
};
const Column = (props) => {
    const { children } = props;
    const ref = React.useRef(null);
    const [textHeight, setTextHeight] = React.useState(0);
    const [textCalc, setTextCalc] = React.useState(false);
    const { textOverflow } = useOverflow(ref, children);
    const mounted = useMounted();
    React.useEffect(() => {
        setTextCalc(true);
        setTextHeight(0);
        setTimeout(() => {
            if (!mounted.current) {
                return;
            }
            if (ref.current) {
                const maxLines = 3;
                const computedStyle = getComputedStyle(ref.current);
                const lineHeight = parseFloat(computedStyle.lineHeight);
                const totalHeight = ref.current.offsetHeight;
                const maxHeight = lineHeight * maxLines;
                const overflows = totalHeight > maxHeight;
                if (overflows) {
                    setTextHeight(maxHeight);
                }
            }
            setTextCalc(false);
        }, 0);
    }, [children, mounted]);
    if (typeof children === 'string' || typeof children === 'number') {
        const text = (_jsx(Typography, { ref: ref, lineClamp: textHeight ? 3 : undefined, height: textHeight ? textHeight : undefined, visibility: textCalc ? 'hidden' : 'visible', overflow: "hidden", textOverflow: "ellipsis", ...props, children: children }));
        return (_jsx(Tooltip, { bypass: !textHeight && !textOverflow, title: children, children: text }));
    }
    return _jsx(_Fragment, { children: children });
};
const getValue = (value) => {
    return Array.isArray(value)
        ? value.filter(Boolean).map((text, i, arr) => (_jsxs(React.Fragment, { children: [text, i < arr.length - 1 && _jsx("br", {})] }, text)))
        : value;
};
const Value = (props) => {
    const refLink = React.useRef(null);
    const { ref } = useSize();
    const { textOverflow } = useOverflow(refLink, props.link?.children);
    const primary = getValue(props.primary);
    const secondary = getValue(props.secondary);
    return (_jsxs(FlexBox, { ref: ref, stack: true, width: "100%", children: [props.link && (_jsx(Tooltip, { title: props.link.children, bypass: !textOverflow, children: _jsx(Link, { ref: refLink, ...props.link, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }) })), primary && (_jsx(TypographyWithTooltip, { variant: "subtitle2", ...props.primaryTypographyProps, children: primary })), secondary && (_jsx(TypographyWithTooltip, { color: "text.secondary", ...props.secondaryTypographyProps, children: secondary }))] }));
};
const WidgetDivider = () => (_jsx(FlexBox, { sx: { px: 0, py: 0.75 }, height: "1px", children: _jsx(Divider, { orientation: "horizontal" }) }));
const Section = (props) => {
    const context = React.useContext(WidgetListContext);
    const InternalRow = Row;
    const sectionContext = React.useMemo(() => context
        ? {
            ...context,
            registerRowWithActions: noopRegisterRowWithActions
        }
        : undefined, [context]);
    return (_jsxs(FlexBox, { stack: true, children: [_jsxs(WidgetListContext.Provider, { value: sectionContext, children: [_jsx(InternalRow, { item: props.item, actions: props.actions, orientation: "horizontal", vAlign: "center", withoutDivider: true, children: _jsx(Column, { variant: "subtitle2", children: props.title }) }), props.children] }), !props.hideDivider && _jsx(WidgetDivider, {})] }));
};
const LoaderBar = () => (_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 20 }));
const Loader = () => {
    return (_jsxs(FlexBox, { px: 2, gap: 1, stack: true, children: [_jsx(LoaderBar, {}), _jsx(LoaderBar, {}), _jsx(LoaderBar, {}), _jsx(LoaderBar, {})] }));
};
Row.displayName = 'Row';
Section.displayName = 'Section';
Row.Value = Value;
WidgetList.Row = Row;
WidgetList.Section = Section;
function isChildRow(element) {
    return Boolean(element && element.type.displayName === Row.displayName);
}
function isChildSection(element) {
    return Boolean(element && element.type.displayName === Section.displayName);
}
